import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { CULTURE_TOPICS } from "@/data/culture";
import { mediaUrl } from "@/lib/api";
import {
  Landmark, Mountain, Drum, UtensilsCrossed, Palette, Music,
  ArrowLeft, ArrowUpRight, Sparkles, Play, Eye, Heart,
} from "lucide-react";

const ICONS = { Landmark, Mountain, Drum, UtensilsCrossed, Palette, Music };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CultureTopic() {
  const { slug } = useParams();
  const topic = CULTURE_TOPICS.find((t) => t.slug === slug);
  const [feed, setFeed] = useState({ videos: [], audios: [] });

  useEffect(() => {
    if (!topic) return;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/feed/${topic.slug}?limit=8`);
        setFeed({ videos: data.videos || [], audios: data.audios || [] });
      } catch {}
    })();
  }, [topic]);

  if (!topic) return <Navigate to="/app/explore" replace />;
  const Icon = ICONS[topic.icon] || Landmark;

  return (
    <div className="pb-20">
      {/* Hero with image */}
      <section className="relative h-72 sm:h-96 overflow-hidden">
        <img src={topic.image} alt={topic.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]/30" />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-10 h-full flex flex-col justify-end pb-8">
          <Link to="/app/explore" data-testid="back-to-explore" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#a3a3a3] hover:text-[#ff4500] mb-4 w-fit">
            <ArrowLeft className="w-3 h-3" /> Esplore
          </Link>
          <div className="flex items-center gap-2 mb-3">
            <Icon className="w-5 h-5" style={{ color: topic.accent }} />
            <span className="text-[10px] uppercase tracking-[0.3em] font-mono" style={{ color: topic.accent }}>
              {topic.titleKreyol}
            </span>
          </div>
          <h1 data-testid="topic-title" className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter">
            {topic.title}
          </h1>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-6 sm:px-10 mt-12">
        <p className="text-lg leading-relaxed text-[#f5f5f5]">{topic.intro}</p>

        {topic.sections.map((s, i) => (
          <div key={i} className="mt-10">
            <div className="text-[10px] uppercase tracking-[0.3em] font-mono mb-2" style={{ color: topic.accent }}>
              ── 0{i + 1}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight">{s.heading}</h2>
            <p className="text-[#a3a3a3] mt-3 leading-relaxed">{s.body}</p>
          </div>
        ))}

        {topic.didYouKnow && (
          <div className="mt-12 border border-[#2a2a2a] bg-[#141414] p-5 flex gap-4">
            <Sparkles className="w-5 h-5 shrink-0 mt-1" style={{ color: topic.accent }} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#a3a3a3]">
                // Did you know
              </div>
              <p className="text-sm text-[#f5f5f5] mt-2 leading-relaxed">{topic.didYouKnow}</p>
            </div>
          </div>
        )}

        {/* Community uploads tagged with this topic */}
        {(feed.videos.length > 0 || feed.audios.length > 0) && (
          <div className="mt-14 border-t border-[#2a2a2a] pt-10" data-testid="community-feed">
            <div className="text-[10px] uppercase tracking-[0.3em] font-mono mb-3" style={{ color: topic.accent }}>
              ── From the community
            </div>
            <h3 className="font-display text-2xl sm:text-3xl tracking-tight">
              Latest about {topic.title.toLowerCase()}
            </h3>

            {feed.videos.length > 0 && (
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {feed.videos.map((v) => (
                  <Link key={v.id} to={`/v/${v.id}`} data-testid={`feed-video-${v.id}`}
                    className="group bg-[#141414] border border-[#2a2a2a] hover:border-[#ff4500]/50 transition-colors overflow-hidden">
                    <div className="relative aspect-video bg-black">
                      {v.thumbnail_url ? (
                        <img src={mediaUrl(v.thumbnail_url)} alt={v.title} className="w-full h-full object-cover" />
                      ) : (
                        <video src={mediaUrl(v.video_url)} className="w-full h-full object-cover" muted preload="metadata" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                        <div className="w-12 h-12 bg-[#ff4500] flex items-center justify-center"><Play className="w-5 h-5 text-white ml-0.5" /></div>
                      </div>
                      <div className="absolute bottom-2 right-3 flex items-center gap-3 text-[11px] text-white/80">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{v.views}</span>
                        <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{v.like_count}</span>
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="text-sm font-display truncate">{v.title}</div>
                      <div className="text-[11px] text-[#a3a3a3] truncate">by {v.owner_name}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {feed.audios.length > 0 && (
              <div className="mt-5 border border-[#2a2a2a]">
                {feed.audios.map((a, i) => (
                  <div key={a.id} className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a] last:border-b-0 hover:bg-[#141414]" data-testid={`feed-audio-${a.id}`}>
                    <div className="flex items-center gap-4 min-w-0">
                      <span className="text-[10px] font-mono text-[#a3a3a3] w-6">{String(i + 1).padStart(2, "0")}</span>
                      <div className="min-w-0">
                        <div className="font-display text-sm truncate">{a.title}</div>
                        <div className="text-xs text-[#a3a3a3] truncate">{a.artist}</div>
                      </div>
                    </div>
                    <audio src={mediaUrl(a.audio_url)} controls className="hidden sm:block max-w-xs" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Contribute */}
        <div className="mt-14 border-t border-[#2a2a2a] pt-10">
          <h3 className="font-display text-2xl tracking-tight">Got something to add?</h3>
          <p className="text-[#a3a3a3] mt-2 text-sm">
            Upload a video, a track, or a story about <span className="text-[#f5f5f5]">{topic.title.toLowerCase()}</span> and help the diaspora tell this story.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/app/videos" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#ff4500] hover:bg-[#ff5722] text-white text-xs uppercase tracking-[0.2em]" data-testid="topic-upload-video">
              Upload video <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/app/music" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500] text-xs uppercase tracking-[0.2em]" data-testid="topic-upload-music">
              Upload music <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/app/profile" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500] text-xs uppercase tracking-[0.2em]" data-testid="topic-promote">
              Promote my page <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
